import { useEffect, useRef, useState } from 'react'
import classNames from 'classnames'

import { EMPTY_FUNCTION } from '../../../../constants/common'
import { EVENT_NAME_ABORT } from '../../../../constants/event'
import { useIntersectionObserver } from '../../../../hooks'
import { getQaSelector } from '../../../../utils/common'
import Text from '../../../Text'
import {
  PDF_VIEWER_THUMBNAIL_HEIGHT,
  PDF_VIEWER_THUMBNAIL_SCROLL_INTO_VIEW_OPTIONS,
  PDF_VIEWER_THUMBNAIL_WIDTH,
  QA_NAME_PDF_VIEWER_THUMBNAIL,
} from '../PdfViewerThumbnails.constants'

import { renderPdfViewerThumbnail } from './PdfViewerThumbnail.utils'

import * as styles from './PdfViewerThumbnail.module.css'

function PdfViewerThumbnail({
  isActive,
  pageNumber,
  pdfDocument,
  rotation,
  onSelectPage,
}) {
  const [thumbnailElement, setThumbnailElement] = useState(null)
  const [isVisible, setVisible] = useState(false)

  const canvasRef = useRef(null)

  function handleObserveVisibility(entries) {
    if (entries.some(entry => entry.isIntersecting)) {
      setVisible(true)
    }
  }

  useIntersectionObserver({
    observerCallback: handleObserveVisibility,
    thresholdElement: thumbnailElement,
  })

  // the panel follows the current page, also when it opens on a far page
  useEffect(() => {
    if (isActive) {
      thumbnailElement?.scrollIntoView(
        PDF_VIEWER_THUMBNAIL_SCROLL_INTO_VIEW_OPTIONS
      )
    }
  }, [isActive, thumbnailElement])

  useEffect(() => {
    if (isVisible && canvasRef.current) {
      const abortController = new AbortController()
      const { signal } = abortController

      pdfDocument
        .getPage(pageNumber)
        .then(page => {
          if (!signal.aborted && canvasRef.current) {
            const renderTask = renderPdfViewerThumbnail({
              canvas: canvasRef.current,
              page,
              rotation,
            })

            signal.addEventListener(EVENT_NAME_ABORT, () => renderTask.cancel())
          }
        })
        // the document may be destroyed mid-flight on fast file switching
        .catch(EMPTY_FUNCTION)

      return () => {
        abortController.abort()
      }
    }
  }, [isVisible, pageNumber, pdfDocument, rotation])

  function handleClick() {
    onSelectPage(pageNumber)
  }

  return (
    <div
      {...getQaSelector({ value: QA_NAME_PDF_VIEWER_THUMBNAIL })}
      className={classNames(styles.PdfViewerThumbnail, {
        [styles.active]: isActive,
      })}
      ref={setThumbnailElement}
      onClick={handleClick}
    >
      <canvas
        className={styles.canvas}
        height={PDF_VIEWER_THUMBNAIL_HEIGHT}
        ref={canvasRef}
        width={PDF_VIEWER_THUMBNAIL_WIDTH}
      />
      <Text className={styles.pageNumber}>{pageNumber}</Text>
    </div>
  )
}

export default PdfViewerThumbnail
